/**
 * PausePopup - Popup de pausa del juego
 *
 * Aparece cuando el jugador presiona ESC durante el juego.
 * Incluye controles de volumen y botones para continuar o salir.
 */

import { Scene, GameObjects } from 'phaser';
import * as Phaser from 'phaser';
import UIPlugin from 'phaser3-rex-plugins/templates/ui/ui-plugin.js';
import { audioManager } from '../core/CoreManagerAudio';
import { LayerDepths } from '../constants/enums';
import { t } from '../i18n/Localization';

export interface PausePopupConfig {
  scene: Scene;
  onResume?: () => void;
  onOptions?: () => void;
  onAbandon?: () => void;
  onExitGame?: () => void;
  onExit?: () => void;
}

export class PausePopup extends GameObjects.Container {
  private rexUI: UIPlugin;
  private overlay: GameObjects.Rectangle;
  private panel: GameObjects.Container;
  private isOpen: boolean = false;
  private prevInputPollRate: number | null = null;
  private prevTopOnly: boolean | null = null;
  private musicVolume: number = 1;
  private sfxVolume: number = 1;
  private prevMusicVolume: number = 1;
  private prevSfxVolume: number = 1;
  private musicValueText: GameObjects.Text | null = null;
  private sfxValueText: GameObjects.Text | null = null;
  private onResume: (() => void) | undefined;
  private onOptions: (() => void) | undefined;
  private onAbandon: (() => void) | undefined;
  private onExitGame: (() => void) | undefined;
  private onExit: (() => void) | undefined;

  private static readonly PANEL_WIDTH = 350;
  private static readonly PANEL_HEIGHT = 320;

  constructor(config: PausePopupConfig) {
    const { width, height } = config.scene.scale;
    super(config.scene, width / 2, height / 2);

    this.rexUI = (config.scene as any).rexUI as UIPlugin;
    this.onResume = config.onResume;
    this.onOptions = config.onOptions;
    this.onAbandon = config.onAbandon;
    this.onExitGame = config.onExitGame;
    this.onExit = config.onExit;

    // Load saved volumes
    this.loadVolumes();

    // Dark overlay covering entire screen and blocking clicks behind the popup
    this.overlay = config.scene.add.rectangle(0, 0, width, height, 0x000000, 0.7)
      .setOrigin(0.5, 0.5)
      .setDepth(0)
      .setInteractive();
    this.overlay.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.event) {
        pointer.event.stopPropagation();
      }
    });
    this.add(this.overlay);

    // Create main panel container
    this.panel = this.createPanel(config.scene);
    this.panel.setDepth(1);
    this.panel.setPosition(0, 0);
    this.add(this.panel);
    this.applyScrollFactor(this);

    // Initially hidden
    this.setVisible(false);
    this.setAlpha(0);
    this.setScale(1);
    this.setScrollFactor(0);
    this.setDepth(LayerDepths.Popup);

    config.scene.add.existing(this);
  }

  private loadVolumes(): void {
    // Load from audioManager (single source of truth)
    this.musicVolume = audioManager.getMusicVolume();
    this.sfxVolume = audioManager.getSfxVolume();
  }

  private saveVolumes(): void {
    // Delegate to audioManager (handles localStorage)
    audioManager.setMusicVolume(this.musicVolume);
    audioManager.setSfxVolume(this.sfxVolume);
  }

  private playButtonSound(type: 'hover' | 'click'): void {
    audioManager.playUISound(type, this.scene);
  }

  private createPanel(scene: Scene): GameObjects.Container {
    const COLOR_PRIMARY = 0x1a2a3a;
    const COLOR_ACCENT = 0x4a9c6d;
    const COLOR_DARK = 0x2a3a4a;

    const panel = scene.add.container(0, 0);

    const bg = this.rexUI.add.roundRectangle(0, 0, PausePopup.PANEL_WIDTH, PausePopup.PANEL_HEIGHT, 16, COLOR_PRIMARY)
      .setStrokeStyle(3, COLOR_ACCENT);
    panel.add(bg);

    const title = scene.add.text(0, -120, t('pause.title'), {
      fontFamily: 'Arial Black',
      fontSize: '36px',
      color: '#ffffff'
    }).setOrigin(0.5);
    panel.add(title);

    const separator = scene.add.rectangle(0, -90, PausePopup.PANEL_WIDTH - 50, 2, COLOR_ACCENT);
    panel.add(separator);

    const musicLabel = scene.add.text(-120, -50, t('pause.music'), {
      fontFamily: 'Arial Black',
      fontSize: '18px',
      color: '#ffffff'
    }).setOrigin(0, 0.5);
    panel.add(musicLabel);

    this.musicValueText = scene.add.text(120, -50, this.musicVolume === 0 ? t('ui.off') : t('ui.on'), {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#4CAF50'
    }).setOrigin(1, 0.5);
    panel.add(this.musicValueText);

    const musicToggle = this.createToggleButton(scene, this.musicVolume === 0 ? t('ui.off') : t('ui.on'), COLOR_DARK, 0, -20, () => {
      this.toggleMusic();
    });
    panel.add(musicToggle);

    const sfxLabel = scene.add.text(-120, 10, t('pause.sfx'), {
      fontFamily: 'Arial Black',
      fontSize: '18px',
      color: '#ffffff'
    }).setOrigin(0, 0.5);
    panel.add(sfxLabel);

    this.sfxValueText = scene.add.text(120, 10, this.sfxVolume === 0 ? t('ui.off') : t('ui.on'), {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#4CAF50'
    }).setOrigin(1, 0.5);
    panel.add(this.sfxValueText);

    const sfxToggle = this.createToggleButton(scene, this.sfxVolume === 0 ? t('ui.off') : t('ui.on'), COLOR_DARK, 0, 30, () => {
      this.toggleSfx();
    });
    panel.add(sfxToggle);

    const resumeBtn = this.createButton(scene, t('pause.resume'), COLOR_ACCENT, -78, 110, () => {
      this.playButtonSound('click');
      this.close();
      if (this.onResume) this.onResume();
    });
    panel.add(resumeBtn);

    const optionsBtn = this.createButton(scene, t('pause.options'), 0x355a84, 78, 110, () => {
      this.playButtonSound('click');
      if (this.onOptions) this.onOptions();
    });
    panel.add(optionsBtn);

    const abandonBtn = this.createButton(scene, t('pause.abandon'), 0x8b5a2b, -78, 162, () => {
      this.playButtonSound('click');
      if (this.onAbandon) this.onAbandon();
    });
    panel.add(abandonBtn);

    const exitBtn = this.createButton(scene, t('pause.exit_game'), 0x8b3a3a, 78, 162, () => {
      this.playButtonSound('click');
      if (this.onExitGame) this.onExitGame();
      else if (this.onExit) this.onExit();
    });
    panel.add(exitBtn);

    panel.setSize(PausePopup.PANEL_WIDTH, PausePopup.PANEL_HEIGHT);
    panel.setScrollFactor(0);
    this.applyScrollFactor(panel);

    return panel;
  }

  private createToggleButton(scene: Scene, text: string, bgColor: number, x: number, y: number, onClick: () => void): GameObjects.Container {
    const container = scene.add.container(x, y);
    const width = 160;
    const height = 36;
    const bg = this.rexUI.add.roundRectangle(0, 0, 160, 36, 10, bgColor)
      .setStrokeStyle(2, 0xffffff);
    const label = scene.add.text(0, 0, text, {
      fontFamily: 'Arial Black',
      fontSize: '16px',
      color: '#ffffff'
    }).setOrigin(0.5);

    container.add([bg, label]);
    container.setSize(width, height);
    bg.setInteractive({ useHandCursor: true });
    container.setScrollFactor(0);
    bg.setScrollFactor(0);
    label.setScrollFactor(0);

    const normalColor = bgColor;
    const hoverColor = Phaser.Display.Color.ValueToColor(bgColor).lighten(20).color;
    const downColor = Phaser.Display.Color.ValueToColor(bgColor).darken(20).color;

    // State tracking para evitar clicks inconsistentes
    let isPressed = false;

    bg.on('pointerover', () => {
      if (isPressed) return;
      bg.setFillStyle(hoverColor);
      bg.setScale(1.03);
      label.setScale(1.03);
      this.playButtonSound('hover');
    });
    bg.on('pointerout', () => {
      if (isPressed) return;
      bg.setFillStyle(normalColor);
      bg.setScale(1);
      label.setScale(1);
    });
    bg.on('pointerdown', () => {
      isPressed = true;
      bg.setFillStyle(downColor);
      bg.setScale(0.97);
      label.setScale(0.97);
    });
    bg.on('pointerup', () => {
      if (!isPressed) return;
      isPressed = false;
      bg.setFillStyle(hoverColor);
      bg.setScale(1.03);
      label.setScale(1.03);
      this.playButtonSound('click');
      onClick();
    });
    bg.on('pointerupoutside', () => {
      isPressed = false;
      bg.setFillStyle(normalColor);
      bg.setScale(1);
      label.setScale(1);
    });

    // Reset on drag
    bg.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!isPressed) return;
      const isInBounds = bg.getBounds().contains(pointer.x, pointer.y);
      if (!isInBounds && isPressed) {
        isPressed = false;
        bg.setFillStyle(normalColor);
        bg.setScale(1);
        label.setScale(1);
      }
    });

    return container;
  }

  private toggleMusic(): void {
    if (this.musicVolume > 0) {
      this.prevMusicVolume = this.musicVolume;
      this.musicVolume = 0;
    } else {
      this.musicVolume = this.prevMusicVolume || 1;
    }
    this.saveVolumes();
    this.refreshVolumeTexts();
  }

  private toggleSfx(): void {
    if (this.sfxVolume > 0) {
      this.prevSfxVolume = this.sfxVolume;
      this.sfxVolume = 0;
    } else {
      this.sfxVolume = this.prevSfxVolume || 1;
    }
    this.saveVolumes();
    this.refreshVolumeTexts();
  }

  private refreshVolumeTexts(): void {
    if (this.musicValueText) {
      this.musicValueText.setText(this.musicVolume === 0 ? t('ui.off') : t('ui.on'));
    }
    if (this.sfxValueText) {
      this.sfxValueText.setText(this.sfxVolume === 0 ? t('ui.off') : t('ui.on'));
    }
  }

private createButton(
  scene: Scene,
  text: string,
  bgColor: number,
  x: number,
  y: number,
  onClick: () => void
): GameObjects.Container {

  const container = scene.add.container(x, y);

  const width = 140;
  const height = 45;

  const bg = this.rexUI.add.roundRectangle(0, 0, width, height, 10, bgColor)
    .setStrokeStyle(2, 0xffffff)
    .setOrigin(0.5);

  const label = scene.add.text(0, 0, text, {
    fontFamily: 'Arial Black',
    fontSize: '20px',
    color: '#ffffff'
  }).setOrigin(0.5);

  container.add([bg, label]);

  container.setSize(width, height);

  bg.setInteractive({ useHandCursor: true });

  container.setScrollFactor(0);
  bg.setScrollFactor(0);
  label.setScrollFactor(0);

  const normalColor = bgColor;
  const hoverColor = Phaser.Display.Color.ValueToColor(bgColor).lighten(20).color;
  const downColor = Phaser.Display.Color.ValueToColor(bgColor).darken(20).color;

  let isPressed = false;

  bg.on('pointerover', () => {
    if (isPressed) return;
    bg.setFillStyle(hoverColor);
    bg.setScale(1.05);
    label.setScale(1.05);
    this.playButtonSound('hover');
  });

  bg.on('pointerout', () => {
    if (isPressed) return;
    bg.setFillStyle(normalColor);
    bg.setScale(1);
    label.setScale(1);
  });

  bg.on('pointerdown', () => {
    isPressed = true;
    bg.setFillStyle(downColor);
    bg.setScale(0.95);
    label.setScale(0.95);
  });

  bg.on('pointerup', () => {
    if (!isPressed) return;
    isPressed = false;
    bg.setFillStyle(hoverColor);
    bg.setScale(1.05);
    label.setScale(1.05);
    this.playButtonSound('click');
    onClick();
  });

  bg.on('pointerupoutside', () => {
    if (!isPressed) return;
    isPressed = false;
    bg.setFillStyle(normalColor);
    bg.setScale(1);
    label.setScale(1);
  });

  return container;
}
  /**
   * Open the pause popup
   */
  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;

    // Reload volumes in case they changed elsewhere
    this.loadVolumes();
    this.refreshVolumeTexts();

    // Ensure hover/out works immediately even if the popup appears under a stationary cursor.
    // Phaser defaults to polling pointers only on move (pollRate = -1).
    if (this.prevInputPollRate === null) {
      this.prevInputPollRate = this.scene.input.pollRate;
      this.scene.input.setPollAlways();
    }
    if (this.prevTopOnly === null) {
      this.prevTopOnly = this.scene.input.topOnly;
      this.scene.input.setTopOnly(true);
    }

    if (this.overlay.input && this.overlay.input.enabled) {
      this.overlay.disableInteractive();
    }

    this.setVisible(true);
    this.setAlpha(1);
    this.setScale(1);
    this.panel.setScale(1);
    this.applyScrollFactor(this);

  }

  /**
   * Close the pause popup
   */
  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    // Restore previous input polling rate
    if (this.prevInputPollRate !== null) {
      const prev = this.prevInputPollRate;
      this.prevInputPollRate = null;
      if (prev === -1) {
        this.scene.input.setPollOnMove();
      } else {
        this.scene.input.setPollRate(prev);
      }
    }
    if (this.prevTopOnly !== null) {
      this.scene.input.setTopOnly(this.prevTopOnly);
      this.prevTopOnly = null;
    }

    // Ensure we don't keep blocking input after the popup closes.
    if (!this.overlay.input || !this.overlay.input.enabled) {
      this.overlay.setInteractive();
    }

    this.setAlpha(0);
    this.setVisible(false);
  }

  /**
   * Toggle pause state
   */
  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Check if popup is open
   */
  getIsOpen(): boolean {
    return this.isOpen;
  }

  /**
   * Apply scrollFactor(0) to all children recursively (for Rex UI elements)
   */
  private applyScrollFactor(element: any): void {
    if (!element) return;

    // Apply to this element if it has setScrollFactor
    if (typeof element.setScrollFactor === 'function') {
      element.setScrollFactor(0);
    }

    // Rex UI sizers have getElement('items') or children array
    if (element.getElement) {
      const items = element.getElement('items');
      if (Array.isArray(items)) {
        items.forEach((item: any) => this.applyScrollFactor(item));
      }
      // Also check background, text, icon, etc.
      ['background', 'text', 'icon', 'thumb', 'track', 'indicator'].forEach(key => {
        const child = element.getElement(key);
        if (child) this.applyScrollFactor(child);
      });
    }

    // Standard Phaser children
    if (element.list && Array.isArray(element.list)) {
      element.list.forEach((child: any) => this.applyScrollFactor(child));
    }
  }
}
